import React, { useCallback, useEffect, useState } from 'react';
import { MiniDrawerWithContext } from 'components/Layout/drawer';
import { makeStyles } from 'tss-react/mui';
import { useTitle } from 'components/utilities/hooks/useTitle';
import {
  primaryTeamSelector,
  userDetailsSelector,
} from 'store/slices/userDetailsSlice';
import { useSelector } from 'react-redux';
import { TEAM_BLOCKS_URL } from 'constants/urls';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import {
  getBlocksColumnsDef,
  SamplesInBlocksColumnsDef,
} from 'components/utilities/AgGridCols/BlocksColumns';
import { Button, Stack, Typography } from '@mui/material';
import { gridThemeStyle } from 'components/OrderDetails/Tabs/constants';
import {
  agGridDefaultColDef,
  getAgGridSelectedChildsUUIDs,
} from 'components/utilities/grid';
import { backendURL } from 'services/backendAPI';
import { DefaultModal } from 'components/Modals/DefaultModal';
import Box from '@mui/material/Box';
import { FormInput } from 'components/FormInputs/FormInput';
import { BlockSampleColumnsDefs } from 'components/utilities/AgGridCols/AgGridColumns';
import {
  CREATE_BLOCK_FROM_SAMPLE_ERROR_MESSAGE,
  FETCH_TEAM_BLOCKS_ERROR_MESSAGE,
  GET_SAMPLES_ERROR,
  MERGE_SAMPLES_TO_BLOCK_ERROR_MESSAGE,
  MERGE_TEAM_BLOCKS_ERROR_MESSAGE,
} from 'constants/errorMessages';
import { useQueryError } from 'utilities/hooks/useQueryError/useQueryError';
import {
  useLazyGetTeamSamplesQuery,
  useMergeBlocksActionMutation,
} from 'store/apis/samplesApi';
import {
  useCreateBlockFromSampleMutation,
  useGetTeamsQuery,
  useLazyGetTeamBlocksQuery,
  useMergeSamplesToBlockMutation,
  useUnmergeBlockSamplesMutation,
  useUpdateBlockMutation,
} from 'store/apis/teamsApi';
import { DEFAULT_SUCCESS_MESSAGE } from 'utilities/hooks/useSnackbar/constants';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { checkIfDiscountExists } from 'components/AntibodiesList/utilities';
import {
  getMultiBlocksMenu,
  getSingeSamplesMenu,
  getSingleBlocksMenu,
} from 'components/BlocksList/utilities';

const useStyles = makeStyles()(() => ({
  newRowClass: {
    backgroundColor: 'lightblue',
  },
}));

export const TeamBlocksPage = () => {
  useTitle('HistoWiz - Blocks List');

  const { classes } = useStyles();

  const userDetails = useSelector(userDetailsSelector);
  const { showSuccess, showError } = useSnackbar();
  const match = useRouteMatch();
  const { teamUUID } = match.params;
  const [isStaff, setIsStaff] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(null);

  const [team, setTeam] = useState(null);
  const [teamName, setTeamName] = useState('');

  const [blocksSelected, setBlocksSelected] = useState([]);

  const [confirmMergeModalOpen, setConfirmMergeModalOpen] = useState(false);
  const [blocksGridAPI, setBlocksGridApi] = useState(null);
  const [samplesGridAPI, setSamplesGridApi] = useState(null);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [samplesQuickFilterText, setSamplesQuickFilterText] = useState('');

  const [getTeamSamples, { data: samplesData }] = useLazyGetTeamSamplesQuery();

  const [getTeamBlocks, { data: blocksData }] = useLazyGetTeamBlocksQuery();

  const [unmergeSamples] = useUnmergeBlockSamplesMutation();
  const [mergeSamplesToBlock] = useMergeSamplesToBlockMutation();
  const [createBlockFromSample] = useCreateBlockFromSampleMutation();
  const [updateBlock] = useUpdateBlockMutation();

  const [mergeBlocksAction, { error: mergeTeamsError }] =
    useMergeBlocksActionMutation();
  useQueryError(mergeTeamsError, MERGE_TEAM_BLOCKS_ERROR_MESSAGE);

  const onBlocksGridReady = useCallback(
    (params) => {
      setBlocksGridApi(params.api);
    },
    [setBlocksGridApi],
  );

  const onSamplesGridReady = useCallback(
    (params) => {
      setSamplesGridApi(params.api);
    },
    [setSamplesGridApi],
  );

  const toggleModal = () => setConfirmMergeModalOpen((prevState) => !prevState);

  const mergeBlocks = ({ rows }) => {
    const rowUUIDs = rows.map((row) => row.uuid);
    mergeBlocksAction({ block_uuids: rowUUIDs })
      .unwrap()
      .then((response) => {
        // Get the selected rows from the grid
        const selectedNodes = blocksGridAPI.getSelectedNodes();
        const selectedData = selectedNodes.map((node) => node.data);
        blocksGridAPI.applyTransaction({ remove: selectedData });

        const updatedRowData = response;
        // update the row data after the merge to have a isNew: true flag
        updatedRowData.forEach((row) => {
          row.isNew = true;
        });

        blocksGridAPI.applyTransaction({ add: updatedRowData, addIndex: 0 });
      })
      .catch(() => showError(MERGE_TEAM_BLOCKS_ERROR_MESSAGE));
  };

  const onMergeSelection = () => {
    mergeBlocks({ rows: blocksSelected });
    toggleModal();
  };

  useEffect(() => {
    // ag-grid caches a some functions, which causes an issue because by default for context we say is not a staff
    if (userDetails.isStaff) {
      setIsStaff(true);
    }
  }, [userDetails]);

  const handleGetTeamBlocks = () => {
    getTeamBlocks({ teamUUID, internal: true })
      .unwrap()
      .then((response) => {
        const firstInstance = response[0];
        if (firstInstance) {
          const { team: instanceTeam } = firstInstance;

          setTeam(instanceTeam);
          setTeamName(instanceTeam.name);
        }
      })
      .catch(() => showError(FETCH_TEAM_BLOCKS_ERROR_MESSAGE));
  };

  useEffect(() => {
    if (!teamUUID) {
      return;
    }

    handleGetTeamBlocks();

    getTeamSamples({ teamUUID, excludeMatchedBlockSamples: true }).catch(() =>
      showError(GET_SAMPLES_ERROR),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamUUID]);

  const getBlocksMenu = (params) => {
    // this function gets cached by ag grid, which is a problem when we actually use
    // react context for is staff and that changes, so here, we pass it into ag-grid as a constantly
    // updating context
    const gridContext = params.context;
    const { isStaff: isStaffContext } = gridContext;
    const row = params.node?.data;

    if (!row || !isStaffContext) return;

    const selectedRows = params.api.getSelectedRows();
    const mergeBlocksCallback = () => {
      setBlocksSelected(selectedRows);
      toggleModal();
    };

    const mergeSamples = () => {
      mergeSamplesToBlock({
        blockUUID: row.uuid,
        samples: samplesGridAPI.getSelectedRows(),
        teamUUID,
      })
        .unwrap()
        .then(() => {
          showSuccess(DEFAULT_SUCCESS_MESSAGE);
        })
        .catch(() => showError(MERGE_SAMPLES_TO_BLOCK_ERROR_MESSAGE));
    };

    const menu =
      selectedRows.length > 1
        ? getMultiBlocksMenu({ mergeBlocks: mergeBlocksCallback })
        : getSingleBlocksMenu({ row, samplesGridAPI, mergeSamples });

    return [...params.defaultItems, ...menu];
  };

  const getSamplesMenu = (params) => {
    const gridContext = params.context;
    const { isStaff: isStaffContext } = gridContext;
    const row = params.node?.data;

    if (!row || !isStaffContext) return;

    const selectedRows = params.api.getSelectedRows();

    const createBlock = () => {
      createBlockFromSample({
        teamUUID,
        sampleUUID: row.uuid,
      })
        .unwrap()
        .then(() => {
          showSuccess(DEFAULT_SUCCESS_MESSAGE);
        })
        .catch(() => showError(CREATE_BLOCK_FROM_SAMPLE_ERROR_MESSAGE));
    };

    const menu =
      selectedRows.length > 1 ? [] : getSingeSamplesMenu({ row, createBlock });

    return [...params.defaultItems, ...menu];
  };

  const handleUnmergeSlides = (sample_uuids) => {
    unmergeSamples({ sample_uuids })
      .unwrap()
      .then(() => {
        showSuccess(DEFAULT_SUCCESS_MESSAGE);
      })
      .catch(() => showError());
  };

  const getSamplesContextMenuItems = (params) => {
    const gridContext = params.context;
    const { isStaff: isStaffContext } = gridContext;
    const rows = params?.node?.data;

    // if not a staff member, don't show anything
    if (!rows || !isStaffContext) {
      return;
    }

    const samplesSelected = getAgGridSelectedChildsUUIDs({
      gridAPI: blocksGridAPI,
    });

    if (!samplesSelected.length) return;

    return [
      {
        name: 'Unmerge Samples from Block',
        action: () => handleUnmergeSlides(samplesSelected),
      },
    ];
  };

  const onQuickFilterText = (event) => {
    setQuickFilterText(event.target.value);
  };

  const onSamplesQuickFilterText = (event) => {
    setSamplesQuickFilterText(event.target.value);
  };

  const detailCellRendererParams = (params) =>
    // provide the Grid Options to use on the Detail Grid
    ({
      detailGridOptions: {
        columnDefs: SamplesInBlocksColumnsDef,
        getContextMenuItems: () => getSamplesContextMenuItems(params),
      },
      // get the rows for each Detail Grid
      getDetailRowData: (rowParams) => {
        rowParams.successCallback(rowParams.data.samples);
      },
    });
  const lisLink = `${backendURL}/lisa/core/team/${team?.id}/`;
  const groupDisplayType = 'multipleColumns';

  const handleVoidBlock = () => {
    updateBlock({
      teamUUID: confirmBlock.team.uuid,
      blockUUID: confirmBlock.uuid,
      body: {
        state: 'voided',
      },
    })
      .unwrap()
      .then(() => {
        setConfirmBlock(null);
        showSuccess(DEFAULT_SUCCESS_MESSAGE);
      })
      .catch(() => showError());
  };

  const isDiscountExists = checkIfDiscountExists(blocksData);
  const blockNames = blocksSelected?.map((block) => block.name).join(', ');

  return (
    <MiniDrawerWithContext header="Blocks List">
      <Typography variant="h4" gutterBottom>
        <a href={lisLink} target="_blank" rel="noreferrer">
          {teamName}
        </a>
      </Typography>
      <Typography variant="h6">
        <a href={lisLink} target="_blank" rel="noreferrer">
          Blocks
        </a>
      </Typography>
      <Box mb={3}>
        <DefaultModal
          open={!!confirmBlock}
          onClose={() => setConfirmBlock(null)}
        >
          <Typography
            variant="h6"
            id="modal-title"
            className={classes.typographyMarginBottom}
            mb={2}
          >
            Void Block {confirmBlock?.name}?
          </Typography>
          <Stack direction="row" justifyContent="end">
            <span className={classes.deleteButton}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleVoidBlock}
              >
                Void Block
              </Button>
            </span>
          </Stack>
        </DefaultModal>
        <FormInput onChange={onQuickFilterText} />
        <div className="ag-theme-balham" style={gridThemeStyle}>
          <AgGridReact
            masterDetail
            rowGroupPanelShow="always"
            detailCellRendererParams={detailCellRendererParams}
            onGridReady={onBlocksGridReady}
            groupDisplayType={groupDisplayType}
            enableSorting
            enableFilter
            enableColResize
            quickFilterText={quickFilterText}
            defaultColDef={agGridDefaultColDef}
            columnDefs={getBlocksColumnsDef({
              classes,
              onBlockUpdate: setConfirmBlock,
            })}
            detailRowHeight={250}
            rowData={blocksData}
            rowSelection="multiple"
            context={{ isStaff }}
            getContextMenuItems={getBlocksMenu}
            rowClassRules={{
              'antibody-has-children': (data) => !data.data,
              'left-padding-exists': (data) => !data.data && isDiscountExists,
              'antibody-has-parent': (data) => data.node.parent.rowIndex,
            }}
            overlayNoRowsTemplate={'<span>No data / Loading</span>'}
          />
        </div>
      </Box>
      <Box>
        <Typography variant="h6">
          <a href={lisLink} target="_blank" rel="noreferrer">
            Samples (No Block Assigned)
          </a>
        </Typography>
        <FormInput onChange={onSamplesQuickFilterText} />
        <div className="ag-theme-balham" style={gridThemeStyle}>
          <AgGridReact
            onGridReady={onSamplesGridReady}
            columnDefs={BlockSampleColumnsDefs}
            rowData={samplesData}
            // onFirstDataRendered={handleFirstDataRendered}
            enableColResize
            quickFilterText={samplesQuickFilterText}
            rowSelection="multiple"
            // enableFillHandle
            // want the grid to stop editing when focus leaves the cell or the grid
            // stopEditingWhenCellsLoseFocus
            fillHandleDirection="y"
            rowDragManaged
            context={{ isStaff }}
            getContextMenuItems={getSamplesMenu}
            rowDrag
            enableRangeSelection
            editable={false}
            suppressClearOnFillReduction
            defaultColDef={agGridDefaultColDef}
            // onCellValueChanged={onCellValueChanged}
            // tooltipShowDelay={0}
            // components={frameworkComponents}
            sideBar
            // onPasteEnd={onPasteEnd}
            // onRowDataUpdated={handleGridRowDataUpdated}
            // onCellDoubleClicked={handleFillHandleDoubleClicked}
            overlayNoRowsTemplate={'<span>No data</span>'}
            // if we don't suppressRowClick, copy and paste keeps on trying to copy
            // the entire row and it really sucks for the entire experience
            suppressRowClickSelection
            suppressCopyRowsToClipboard
            // suppressNoRowsOverlay={isLoading}
          />
        </div>
      </Box>
      {blocksSelected && (
        <DefaultModal open={confirmMergeModalOpen} onClose={toggleModal}>
          <Typography
            variant="h6"
            id="modal-title"
            className={classes.typographyMarginBottom}
          >
            Merge Blocks: {blockNames}?
          </Typography>
          <Box mt={1} mb={2}>
            <Typography variant="subtitle1" id="simple-modal-description">
              Warning : This will permanently merge and delete the other
              selected blocks.
            </Typography>
          </Box>
          <Stack direction="row" justifyContent="end">
            <span className={classes.deleteButton}>
              <Button
                variant="contained"
                color="primary"
                onClick={onMergeSelection}
              >
                Merge Blocks
              </Button>
            </span>
          </Stack>
        </DefaultModal>
      )}
    </MiniDrawerWithContext>
  );
};

export const BlocksPage = () => {
  const { data: teams, error } = useGetTeamsQuery();

  useQueryError(error);

  const primaryTeam = useSelector(primaryTeamSelector) || teams?.[0];
  const history = useHistory();

  useEffect(() => {
    if (!primaryTeam) {
      return;
    }
    const url = TEAM_BLOCKS_URL.replace(':teamUUID', primaryTeam.uuid);
    // found primary URL, redirecting
    history.replace(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryTeam]);

  return <MiniDrawerWithContext header="Blocks List" />;
};
